<template>
  <header v-if="active">
    <section v-if="!loadingNavbar"
      class="nav-container no-print"
      role="navigation">
      <!-- Navbar -->
      <nav ref="navbar" class="nav is-flex">
        <!-- More Burger -->
        <div class="nav-item is-hoverable explore-burger gtm-nav-burger step-0-makehome-obm"
            @click="toggleExplore">
          <button v-if="!isExploring"
                  class="explore-button is-small cursor-pointer"
                  aria-label="Navigation menu">
            <i class="fal fa-bars gtm-nav-burger" aria-hidden="true"/>
          </button>
          <button v-if="isExploring"
                  class="explore-button is-small cursor-pointer"
                  aria-label="Exit navigation menu">
            <i class="fal fa-times" aria-hidden="true" />
          </button>
        </div>
        <template v-if="!isComingSoon">
          <!-- Search Button -->
          <div v-if="showSearch" class="nav-item gtm-nav-search step-1-makehome-obm is-hidden-desktop mobile-search"
              :class="{'logged-out': !isLoggedIn}"
              @click="toggleSearch(), exitExplore()">
            <button v-if="!isSearching"
                    class="search-button is-small cursor-pointer gtm-nav-search"
                    aria-label="Search">
              <i class="fal fa-search gtm-nav-search" aria-hidden="true"/>
            </button>
            <button v-if="isSearching"
                    class="search-button is-small cursor-pointer gtm-nav-search"
                    aria-label="Exit search">
              <i class="fal fa-times gtm-nav-search" aria-hidden="true"/>
            </button>
          </div>
        </template>
        <!-- Start Now Left-->
        <li v-if="showStartNow" class="start-now-wrapper button-wrapper is-flex is-hidden-desktop ml-3"
            :data-balloon="enterNow"
            data-balloon-pos='down'>
          <router-link class="start-now-button is-small gtm-nav-start-now step-2-makehome-obm step-2-makehome-obd"
                       :class="{'shorten ysc': $isYSC, 'disabled' : isAfterAjasReleaseDate}"
                       @click.native.prevent="checkStartButtonNavigation"
                       @keyup.enter.native.prevent="checkStartButtonNavigation"
                       to=""
                       tabindex="0"
                       aria-label="Start a new project">
              <span class="hide-start-menu">
                {{ $t('start') }}
              </span>
            <span class="hide-start-menu-mobile">
                <i class="fal fa-plus no-print"
                   aria-hidden="true"/>
                <span class="text visually-hidden">
                  {{ $t('start') }}
                </span>
              </span>
          </router-link>
        </li>
        <!-- Logo -->
        <div v-if="!isSearching && !hideLogo"
             class="nav-title gtm-nav-wordmark"
             @click="exitExplore(), hideMenu(), selectLink('Explore')"
             @keyup.enter="exitExplore(), hideMenu(), selectLink('Explore')">
          <a class="gtm-nav-wordmark unselectable is-flex"
             :href="$isEngCom ? '/' : `${this.$webRoot}/`"
             aria-label="Click to explore on home"
             @click.prevent="clickLogo">
            <div class="logo-container">
              <img class="logo gtm-nav-wordmark" :style="{'max-height' : navbarSettings.logoMaxHeight + 'px'}" :src="logo" :alt="logoAlt" />
            </div>
            <div class="wordmark-container" qa="site-logo">
              <img class="wordmark gtm-nav-wordmark"
                   :style="wordmarkStyle"
                   :src="wordmark" :alt="logoAlt" />
            </div>
          </a>
        </div>
        <!-- Search Bar in Focus -->
        <div v-if="isSearching" class="search-bar" :class="{'logged-out': !isLoggedIn}">
          <label class="is-hidden">{{$t('navBar.search')}}</label>
          <input type="text" :placeholder="searchPlaceholder"
                 class="search-field" v-model="searchQuery"
                 @keyup.enter="search"
                 @keyup.esc="isSearching = false"
                 ref="search" autocomplete="off"
                 data-lpignore="true" />
        </div>
        <ul class="nav-start is-flex flex-grow is-hidden-touch">
          <!-- NavToolbar Links -->
          <li v-if="!isSearching" class="category-nav unselectable" :key="navigationKey">
            <ul class="category-list is-flex">
              <li class="category-item" v-for="navbarItem in navbarItems" :key="navbarItem.id">
                <div v-if="navbarItem.displayTextKey === 'navBar.topics' && isEngCom">
                  <a class="category-link link-bold chevron-color cursor-pointer is-flex"
                     :class="{'active-more': isExploringTopic}"
                     @click="toggleExploreTopic"
                     @keyup.enter="toggleExploreTopic"
                     tabindex="0"
                     aria-label="Toggle the 'Explore More' dropdown">
                    {{ $t("navBar.topics") }}
                    <i v-if="!isExploringTopic" class="fal fa-chevron-down" aria-hidden="true" />
                    <i v-if="isExploringTopic" class="fal fa-chevron-up" aria-hidden="true" />
                  </a>
                  <explore-dropdown @exitExplore="exitExplore"
                                    @selectLink="selectLink"
                                    :navLinks="navbarItems"
                                    :moreLinks="moreItems"
                                    :isTopic="true"
                                    :isExploring="isExploringTopic"
                                    :isMoreDropdown="true"
                                    :showCategoriesMore="navbarSettings && navbarSettings.showCategoriesMore ? navbarSettings.showCategoriesMore : false"
                                    direction="down"
                                    @openLink="openLink"
                                    @openMakeHome="openMakeHome"
                                    @forceRerender="forceRerender"/>
                </div>

                <div v-if="navbarItem.maxViewWidth === 0 || viewportWidth > navbarItem.maxViewWidth">
                  <router-link v-if="!navbarItem.subItems && navbarItem.displayTextKey !== 'navBar.topics'"
                     class="category-link link-underline link-bold cursor-pointer is-flex"
                     :class="{'link-exact-active' : navbarItem.name === selectedLink}"
                     @click.native.prevent="followLink(navbarItem), selectLink(navbarItem.name)"
                     @keyup.enter.native.prevent="followLink(navbarItem), selectLink(navbarItem.name)"
                     to=""
                     :aria-label="`Go to the ${navbarItem.name} page`" tabindex="0">
                    <span :ref="navbarItem.routeName">{{ navBarString(navbarItem.displayTextKey) }}</span>
                  </router-link>
                  <a v-if="navbarItem.subItems"
                               class="category-link link-bold chevron-color cursor-pointer is-flex"
                               @click="toggleNavSubItemExplore(navbarItem)"
                               @keyup.enter="toggleNavSubItemExplore(navbarItem)"
                               tabindex="0" aria-label="Toggle the Navigation Item Dropdown">
                    {{ navBarString(navbarItem.displayTextKey) }}
                    <span :key="dropdownExploreKey">
                      <i v-if="!navbarItem.isExploring" class="fal fa-chevron-down" aria-hidden="true"/>
                      <i v-if="navbarItem.isExploring" class="fal fa-chevron-up" aria-hidden="true"/>
                    </span>
                  </a>
                  <explore-dropdown
                      :key="dropdownExploreKey"
                      @exitExplore="closeExploreItem(navbarItem)"
                      @selectLink="selectLink"
                      :isExploring="navbarItem.isExploring ? navbarItem.isExploring : false "
                      :isSubItemDropdown="true"
                      :subItems="navbarItem.subItems"
                      direction="down"
                      @openLink="openLink"
                      @forceRerender="forceRerender"/>
                </div>
              </li>
              <li v-if="moreItems.length > 0 || showMoreViewSize > viewportWidth" class="category-item more-dropdown category-more"
                :class="{'category-more-isef': $isIsef}" qa="5More">
                <a class="category-link link-bold chevron-color cursor-pointer is-flex"
                             :class="{'active-more': isExploringMore}"
                             @click="toggleExploreMore"
                             @keyup.enter="toggleExploreMore"
                             tabindex="0"
                             aria-label="Toggle the 'Explore More' dropdown">
                    {{ $t("navBar.more") }}
                  <i v-if="!isExploringMore" class="fal fa-chevron-down" aria-hidden="true" />
                  <i v-if="isExploringMore" class="fal fa-chevron-up" aria-hidden="true" />
                </a>
                <explore-dropdown @exitExplore="exitExplore"
                                 @selectLink="selectLink"
                                 :navLinks="navbarItems"
                                 :moreLinks="moreItems"
                                 :isExploring="isExploringMore"
                                 :isMoreDropdown="true"
                                 :showCategoriesMore="false"
                                 direction="down"
                                 @openLink="openLink"
                                 @openMakeHome="openMakeHome"
                                 @forceRerender="forceRerender"/>
              </li>
            </ul>
          </li>
        </ul>
        <ul class="nav-end is-flex" v-if="!isComingSoon">
          <!-- Start Now Right -->
          <li v-if="showStartNow" class="start-now-wrapper button-wrapper is-flex is-hidden-touch"
              :data-balloon="enterNow"
              data-balloon-pos='down'>
            <router-link class="start-now-button is-small gtm-nav-start-now step-2-makehome-obm step-2-makehome-obd"
                         :class="{'shorten ysc': $isYSC, 'disabled' : isAfterAjasReleaseDate}"
                         @click.native.prevent="checkStartButtonNavigation"
                         @keyup.enter.native.prevent="checkStartButtonNavigation"
                         to=""
                         tabindex="1" qa="start-project-navtoolbar">
              <span class="hide-start-menu">
                {{ $t('start') }}
              </span>
              <span class="hide-start-menu-mobile">
                <i class="fal fa-plus no-print"
                   aria-hidden="true"/>
                <span class="text visually-hidden">
                  {{ $t('start') }}
                </span>
              </span>
            </router-link>
          </li>
          <!-- Search Button -->
          <li v-if="showSearch" class="nav-item gtm-nav-search step-1-makehome-obd desktop-search is-hidden-mobile"
              :class="{'logged-out': !isLoggedIn}"
              @click="toggleSearch(), exitExplore()">
            <button v-if="!isSearching"
                    class="search-button is-small cursor-pointer gtm-nav-search"
                    :class="{'make-search-button' : !$isYSC}"
                    aria-label="Search" qa="search-navtoolbar">
              <i class="fal fa-search gtm-nav-search" aria-hidden="true"/>
            </button>
            <button v-if="isSearching"
                    class="search-button is-small cursor-pointer gtm-nav-search"
                    aria-label="Exit search">
              <i class="fal fa-times gtm-nav-search" aria-hidden="true"/>
            </button>
          </li>
          <!-- Chat Button -->
          <li v-if="showChat"
              class="nav-item gtm-nav-mail u-got-mail"
              :class="{'u-got-mail': hasMail}"
              @click.prevent="openLink('/inbox')">
            <router-link class="mail-button is-small gtm-nav-mail cursor-pointer step-6-makehome-obd step-5-makehome-obm"
               aria-label="Inbox"
               to="/inbox"
               qa="inbox-navtoolbar">
              <i class="fal fa-comments-alt gtm-nav-mail" aria-hidden="true"/>
              <div v-if="hasMail" class="unread-count is-flex gtm-nav-mail" :class="{'ysc': $isYSC}">
                {{mailCount}}
              </div>
            </router-link>
          </li>
          <li v-if="showNotificationsIcon" class="nav-item notification-bell"
              tabindex="0"
              @click="showNotifications"
              @keyup.enter.space="showNotifications"
              aria-label="Press enter to select the notification bell">
            <div v-if="notificationsCount > 0">
              <div class="unread-count unread-count-bell is-flex" :class="{'ysc': $isYSC}">
                {{notificationsCount}}
              </div>
            </div>
            <i class="far fa-bell"></i>
          </li>
          <!-- Sign In -->
          <li v-if="isLoggedIn" class="profile-dropdown nav-item gtm-nav-dropdown"
              v-click-outside="hideMenu"
              @keyup.enter="toggleMenu(), exitExplore()"
              aria-label="Press enter to toggle profile submenu"
              tabindex="0">
            <!-- Profile Avatar Desktop - hover to see menu, click to go to profile -->
            <a class="profile-burger nav-item gtm-nav-dropdown"
               @click="toggleMenu()"
               qa="profile-icon-navtoolbar">
              <figure class="profile-img gtm-nav-dropdown unselectable">
                <img :src="avatar"
                     alt="Profile Icon"
                     class="gtm-nav-dropdown">
              </figure>
            </a>
            <!-- Profile Avatar Touch - click to see menu -->
            <button class="profile-burger touch nav-item gtm-nav-dropdown"
                    @click="toggleMenu">
              <figure class="profile-img unselectable gtm-nav-dropdown">
                <img :src="avatar"
                     alt="Profile Icon"
                     class="gtm-nav-dropdown"
                     qa="profile_icon_mbl">
              </figure>
            </button>
            <!-- Profile Dropdown Menu -->
            <ul class="dropdown-menu"
                :class="{'is-shown': showMenu}"
                qa="profile-dropdown"
                @click="toggleMenu()"
                >
              <li>
                <router-link class="nav-item gtm-nav-profile"
                   @click.prevent="openLink('/profile?activeTab=TabProjectBoards')"
                   @keyup.enter.stop="openLink('/profile?activeTab=TabProjectBoards'), hideMenu()"
                   tabindex="0" :to="`/profile?activeTab=TabProjectBoards`"
                   aria-label="Go to Profile Page" qa="profile-opt">
                  {{$t('navBar.profile')}}
                </router-link>
              </li>
              <li v-if="!this.$isYSC || this.isAdmin || (this.currentUser.isPortal && this.isLoggedIn)">
                <router-link class="nav-item gtm-nav-my-projects"
                   @click.prevent="openViewAllPages('/viewAll/?profile=mine'),
                    selectLink('My Projects')"
                   @keyup.enter.stop="openViewAllPages('/viewAll/?profile=mine'),
                    hideMenu(), selectLink('My Projects')"
                   tabindex="0" :to="`/viewAll/?profile=mine`"
                   :aria-label="$t('navBar.projectsPage') " qa="my-projects-opt">
                  {{ $t('navBar.myProjects') }}
                </router-link>
              </li>
              <li>
                <router-link v-if="isUserApprover" class="nav-item gtm-nav-my-projects"
                   @click.prevent="openViewAllPages('/viewAll/?profile=submissions'),
                    selectLink('My Projects')"
                   @keyup.enter.stop="openViewAllPages('/viewAll/?profile=submissions'),
                    hideMenu(), selectLink('My Projects')"
                   tabindex="0" :to="`/viewAll/?profile=submissions`"
                   :aria-label="$t('navBar.projectsPage') " qa="my-projects-opt">
                  {{ $t('navBar.submissions') }}
                </router-link>
              </li>
              <li style="display: none;">
                <router-link v-if="isUserJudge" class="nav-item gtm-nav-my-projects"
                             @click.prevent="openLink('/adminJudge'),
                    selectLink('My Projects')"
                             @keyup.enter.stop="openLink('/adminJudge'),
                    hideMenu()"
                             tabindex="0" :to="`/adminJudge`"
                             :aria-label="$t('navBar.projectsPage') " qa="my-projects-opt">
                  Admin Judge
                </router-link>
              </li>
              <li>
                <router-link class="nav-item gtm-nav-contributed-to"
                   @click.prevent="openViewAllPages('/viewAll/?profile=contributed'),
                    selectLink(null)"
                   @keyup.enter.stop="openViewAllPages('/viewAll/?profile=contributed'),
                    hideMenu(), selectLink(null)"
                   tabindex="0" :to="`/viewAll/?profile=contributed`"
                   :aria-label="$t('navBar.contributionsPage') " qa="contributed-to-opt">
                  {{$t('navBar.contributedTo')}}
                </router-link>
              </li>
              <li>
                <router-link class="nav-item gtm-nav-following"
                   @click.prevent="openViewAllPages('/viewAll/?profile=bookmarks'),
                    selectLink(null)"
                   @keyup.enter.stop="openViewAllPages('/viewAll/?profile=bookmarks'),
                    hideMenu(), selectLink(null)"
                   tabindex="0" :to="`/viewAll/?profile=bookmarks`"
                   :aria-label="$t('navBar.bookmarksPage') " qa="my-bookmarks-opt">
                  {{$t('navBar.myBookmarks')}}
                </router-link>
              </li>
              <li>
                <router-link class="nav-item gtm-nav-following"
                   @click.prevent="isEngCom ? openViewAllPages('viewAll/?category=Recent Activity') :
                    openLink('/feed', true), selectLink(null)"
                   @keyup.enter.stop="isEngCom ? openViewAllPages('viewAll/?category=Recent Activity') :
                    openLink('/feed'), hideMenu(), selectLink(null)"
                   tabindex="0" :to="isEngCom ? `viewAll/?category=Recent Activity` : `/feed`"
                   :aria-label="`Go to ${isEngCom ? 'Recent Activities' : 'Activity Feeds'} Page`"
                   qa="act-feed-opt"
                   v-if="showActivityFeed">
                  {{ isEngCom ? $t('navBar.recentActivity') : $t('navBar.activityFeed') }}
                </router-link>
              </li>
              <li>
                <router-link v-if="isAdmin || isAnalytic || isCurator || isResourceEditor" class="nav-item"
                   @click.prevent="openLink('/admin', true)"
                   @keyup.enter.stop="openLink('/admin'), hideMenu()"
                   tabindex="0" :to="`/admin`"
                   :aria-label="$t('navBar.adminPage') ">
                  Admin
                </router-link>
              </li>
              <li>
                <a v-if="wasAdmin" class="nav-item"
                   @click="openLink('/su/exit')"
                   @keyup.enter.stop="openLink('/su/exit'), hideMenu()"
                   tabindex="0" :aria-label="$t('navBar.returnToAdminPage') ">
                  {{$t('navBar.returnToAdminPage')}}
                </a>
              </li>
              <!--              re-enable when the banner feature is complete-->
              <!--              <li v-if="currentUser.isBannerManager" >-->
              <!--                <a class="nav-item"-->
              <!--                  @click.prevent="openLink('/contests/banners', true)"-->
              <!--                  @keyup.enter.stop="openLink('/contests/banners'), hideMenu()"-->
              <!--                  tabindex="0" :href="`/contests/banners`"-->
              <!--                  :aria-label="$t('navBar.manageBanners') ">-->
              <!--                  Manage Banners-->
              <!--                </a>-->
              <!--              </li>-->
              <li v-if="currentUser.isIFrameManager" >
                <router-link class="nav-item"
                   @click.prevent="openLink('/contests/iframes', true)"
                   @keyup.enter.stop="openLink('/contests/iframes'), hideMenu()"
                   tabindex="0" :to="`/contests/iframes`"
                   :aria-label="$t('navBar.manageIFrames') ">
                  Manage IFrames
                </router-link>
              </li>
              <li v-if="showMyClasses">
                <router-link class="nav-item gtm-nav-profile"
                             @click.prevent="openLink('/profile?activeTab=TabMyClasses')"
                             @keyup.enter.stop="openLink('/profile?activeTab=TabMyClasses'), hideMenu()"
                             tabindex="0" :to="`/profile?activeTab=TabMyClasses`"
                             aria-label="Go to Profile Page" qa="profile-opt">
                  {{$t('navBar.manageClass')}}
                </router-link>
              </li>
              <li>
                <a class="nav-item gtm-nav-logout"
                   @click="showLogout = true, exitExplore()"
                   @keyup.enter.stop="showLogout = true, hideMenu(), exitExplore()"
                   tabindex="0" aria-label="Log out" qa="log-out-opt">
                  {{$t('navBar.logout')}}
                </a>
              </li>
            </ul>
          </li>
          <li class="login-wrapper is-flex gtm-nav-login"
              v-if="!isLoggedIn">
            <button class="login-button button is-primary is-small gtm-nav-login"
                    @click.stop="showLogin(); exitExplore()"
                    qa="auth-start">
              {{$t('navBar.login')}}
            </button>
          </li>
        </ul>
      </nav>
      <div v-if="this.isStory" id="progress-bar" :class="{'make-progress-bar': isProjects }"></div>
    </section>
    <div class="navbar-spacer" :class="{'hidden-mobile' : !active}" />
    <notifications />
    <explore-dropdown
        @exitExplore="exitExplore"
        :isExploring="isExploring"
        direction="toRight"
        @selectLink="selectLink"
        :navLinks="navbarItems"
        :moreLinks="moreItems"
        :showCategoriesMore="navbarSettings && navbarSettings.showCategoriesMore ? navbarSettings.showCategoriesMore : false"
        :isMoreDropdown="true"
        @openLink="openLink"
        @openMakeHome="openMakeHome"
        @forceRerender="forceRerender" />
    <explore-background @exitExplore="exitExplore"
                        :isExploring="isExploring || isExploringMore || isExploringTopic || isExploringDropdown" />
    <login-modal ref="loginModal"
                 @modalDone="modalDone"
                 :startButtonClicked="this.startButtonClicked"
                 @startButtonClicked="startButtonAction" />
    <source-modal ref="sourceModal"
                  @modalDone="modalDone" :isNewUser="isNewUser"/>
    <feedback-modal v-if="showFeedbackModal" :templateActive="showChooseTemplateModal" />
    <categories-modal ref="categoriesModal"
                      isNavBar="true" @modalDone="modalDone" />
    <modal-onboarding ref="onboardingModal"
                      @modalDone="modalDone" />
    <show-teacher-code ref="teacherModal" @closeTeacherCodeModal="closeTeacherCodeModal"></show-teacher-code>
    <show-student-sign-up v-if="showStudentCode" @closeStudentCode="closeStudentCode"></show-student-sign-up>
    <logout v-if="showLogout"
            @cancel="showLogout = false"
            @logout="logout"
            @modalDone="modalDone" />
    <start-project v-if="!isMobile"
                   @startButtonClicked="startButtonAction"/>
    <choose-template v-if="showChooseTemplateModal"
                     @close="close" />
    <modal-video-tour @clicked="openChooseTemplateModal" />
    <modal-update-profile ref="updateProfileModal"
                          @openChooseTemplateModal="openChooseTemplateModal"
                          :signingUpThroughTemplate="signingUpThroughTemplate"
                          @modalDone="modalDone" />
    <modal-start-project />
    <notifications-dropdown v-if="isLoggedIn" v-click-outside="onClickOutsideNotifications" ref="notificationsDropdown"></notifications-dropdown>
    <modal-custom
        v-if="!isLoggedIn && $isGirlsAndSteam && $route.name !== 'contact' && $route.name !== 'landingRedirect' && $route.name !== 'resetPassword'"
        :hasFooter="true"
        title='Login'
        text="To view this content, please sign up or log in by clicking below."
        successText="Login"
        @modalSuccess="showLoginModal"
    ></modal-custom>
    <modal-judging-registration-tour v-if="!isJudgingAdmin"/>
    <ysc-pre-registration-modal v-if="!isLoggedIn" />
    <fade-message :message="notificationMessage" :messageTimeout="10000" @clearMessage="clearNotificationMessage"></fade-message>
  </header>
</template>

<script>
import navbarApi from '@/api/navbarItems'
import users from '@/api/users'

import ExploreBackground from './ExploreBackground'
import ExploreDropdown from './ExploreDropdown'
import navbarLinkMixin from './navbarLinkMixin'

import Notifications from '@/components/Controls/Notifications'
import LoginModal from '@/components/Modals/Login'
import SourceModal from '@/components/Modals/SignupSurvey'
import FeedbackModal from '@/components/Modals/FeedbackSurvey'
import CategoriesModal from '@/components/Modals/Categories'
import Logout from '@/components/Modals/Logout'
import StartProject from '@/components/Controls/StartProject'
import ModalOnboarding from '@/components/Modals/Onboarding'
import ModalVideoTour from '@/components/Modals/VideoTour'
import ModalJudgingRegistrationTour from '@/components/Modals/Onboarding/judgingRegistration'
import ModalUpdateProfile from '@/components/Modals/UpdateProfile'
import ModalStartProject from '@/components/Modals/StartProject'
import NotificationsDropdown from './NotificationsDropdown'
import ChooseTemplate from '@/components/Modals/ChooseTemplate'
import ModalCustom from '@/components/Modals/Custom'
import authlib from '@/libs/auth'
import breakpoints from '@/libs/breakpoints'
import socket from '@/libs/socket'
import imageProxy from '@/libs/image-proxy'
import userAgent from '@/libs/user-agent'
import FadeMessage from '@/components/Controls/FadeMessage'
// import versionCheck from '@/libs/version-check'
import ShowTeacherCode from '@/components/Modals/TeacherCode'
import ShowStudentSignUp from '@/components/Modals/StudentSignUpByCode'
import ajas from '@/mixins/ajas'
import theme from '@/mixins/theme'
import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'
import link from '@/mixins/link'
import newFeatureTooltip from '@/mixins/new-feature-tooltip'
import YscPreRegistrationModal from '@/components/Modals/ysc'

import auth0 from 'auth0-js'
import axios from 'axios'
import i18n from '@/i18n'
import ClickOutside from 'vue-click-outside'
import localStorage from 'store'
import userTypes from '@/mixins/user-types'

export default {
  components: {
    Notifications,
    LoginModal,
    SourceModal,
    Logout,
    CategoriesModal,
    FeedbackModal,
    FadeMessage,
    StartProject,
    ModalOnboarding,
    ChooseTemplate,
    ModalVideoTour,
    ModalUpdateProfile,
    ModalStartProject,
    NotificationsDropdown,
    ExploreDropdown,
    ExploreBackground,
    ModalCustom,
    ModalJudgingRegistrationTour,
    ShowTeacherCode,
    ShowStudentSignUp,
    YscPreRegistrationModal
  },
  mixins: [auth, componentState, link, newFeatureTooltip, ajas, theme, navbarLinkMixin, userTypes],
  data: function () {
    return {
      modalIndex: 0,
      isProjects: this.$isProjects,
      isEngCom: this.$isEngCom,
      showMenu: false,
      showLogout: false,
      isSearching: false,
      searchQuery: '',
      subscriptions: { message: false, user: false },
      checkedUnread: false,
      signupSource: 'LOGIN:{PATH}',
      passwordSet: false,
      isExploring: false,
      isExploringDropdown: false,
      isExploringMore: false,
      isExploringTopic: false,
      previousPath: '',
      showChooseTemplateModal: false,
      selectedLink: null,
      signingUpThroughTemplate: false,
      modals: [],
      signupModals: [],
      openTemplateAfterSignup: false,
      linkWidths: [],
      loadingNavbar: true,
      isUserApprover: false,
      isUserJudge: true,
      startButtonClicked: false,
      navbarItems: [],
      moreItems: [],
      navbarSettings: null,
      unreadNotificationsCount: 0,
      partnerQuestions: [],
      showMoreViewSize: 0, // this is used for sites that do not have items in the more dropdown, but we still want to show navlinks there on smaller screen sizes
      navigationKey: '',
      errorMessage: '',
      hasMore: false,
      limit: 12,
      offset: 0,
      filterQuestionsByType: false,
      showTeacherCode: false,
      teacherCode: false,
      showStudentCode: false,
      isNewUser: false,
      notificationMessage: ''
    }
  },
  watch: {
    '$store.getters.userCategories': function (value) {
      if (this.isLoggedIn) {
        if (value.userCategories !== undefined) {
          this.userCategories = value.userCategories.slice().sort((a, b) => a.name.localeCompare(b.name))
        }
      }
    },
    $route: function () {
      this.updateSelectedLink()
      if (this.$route.query.notifications === 'on') {
        if (!this.isLoggedIn) {
          this.$store.dispatch('showLoginModal')
        } else {
          this.showNotifications()
        }
      }
    },
    navbarItems: function () {
      this.updateSelectedLink()
    },
    selectedLink: function () {
      if (this.selectedLink) {
        this.navigationKey = this.selectedLink
      }
    },
    loginActive: function (active) {
      if (active) {
        this.showModal()
      }
    },
    '$route.query.follow': function (hasNewFollow) {
      if (hasNewFollow) {
        this.tagAndAddToGroup()
      }
    },
    isLoggedIn: function () {
      this.isLoggedInUpdated()
    },
    // TODO This is here to handle YSC Judge Registration.
    // Refactor Custom Question to use Store!
    // In a edge case when logged in user became Judge, the update of the User Type happens after mount and isLoggedIn
    // thus it won't be able to trigger the modal, adding watch for currentUser so that when user type get updated
    // it triggers the custom question modal
    currentUser: function (newVal, oldVal) {
      if (this.isLoggedIn) {
        if ((this.$isYSC || this.$isOhioScience) && ([this.UserTypeJudge, this.UserTypeTeacher].includes(this.$route.query.userType) || [this.UserTypeTeacher, this.UserTypeJudge].includes(this.$store.getters.currentUser.type))) {
          if (newVal.type !== oldVal.type) {
          }
        }
      }
    }
  },
  computed: {
    isJudgingAdmin: function () {
      return this.currentUser.isJudgingAdmin
    },
    showActivityFeed: function () {
      return !this.$isYSC
    },
    isStudentType: function () {
      return ((this.currentUser.type?.includes(this.UserTypeStudent) || this.$route.query.userType?.includes(this.UserTypeStudent)) && (this.$isOhioScience || this.$isYSC))
    },
    isRouterTeacherType: function () {
      return this.$route.query.userType?.includes(this.UserTypeTeacher) && this.$isOhioScience
    },
    isTeacherType: function () {
      return (this.currentUser.type?.includes(this.UserTypeTeacher) || this.$route.query.userType?.includes(this.UserTypeTeacher)) && this.$isOhioScience
    },
    isMentorType: function () {
      return this.currentUser.type?.includes(this.UserTypeMentor) || this.$route.query.userType?.includes(this.UserTypeMentor)
    },
    isJudgeType: function () {
      return this.currentUser.type?.includes(this.UserTypeJudge) || this.$route.query.userType?.includes(this.UserTypeJudge)
    },
    loginActive: function () {
      if (this.$isYSC) {
        return this.$store.getters.modalIsVisible('login') && !this.isStudent
      } else {
        return this.$store.getters.modalIsVisible('login')
      }
    },
    loginModalData: function () {
      return this.$store.getters.modalData('login') || {}
    },
    avatar: function () {
      return imageProxy.thumbnail(this.currentUser.avatar)
    },
    hasMail: function () {
      return this.mailCount > 0
    },
    hasNotifications: function () {
      return this.$store.getters.unreadNotificationsCount
    },
    mailCount: function () {
      return Math.min(this.$store.getters.unreadConversationsCount, 99)
    },
    notificationsCount: function () {
      return Math.min(this.$store.getters.unreadNotificationsCount, 99)
    },
    hiddenMobile: function () {
      return this.$route.meta && (this.$route.meta.hideMobileNav === true)
    },
    active: function () {
      return !this.$route.meta || (this.$route.meta.hideNavbar !== true)
    },
    hideLogo: function () {
      return this.$route.query.iframe !== undefined
    },
    logo: function () {
      if (this.navbarSettings && this.navbarSettings.logo) {
        return this.navbarSettings.logo
      } else {
        return '/static/projectboard/logo/logo-pb.png'
      }
    },
    wordmark: function () {
      if (this.navbarSettings && this.navbarSettings.wordmark) {
        return this.navbarSettings.wordmark
      } else {
        return '/static/projectboard/navigation/logo.svg'
      }
    },
    logoAlt: function () {
      return this.isEngCom ? 'Engineering.com' : ''
    },
    searchPlaceholder: function () {
      return this.isEngCom ? 'search stories and people' : i18n.t('search.placeholder')
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    isTouch: function () {
      return breakpoints.isTouch()
    },
    appName: function () {
      return process.env.APP_NAME.toLowerCase()
    },
    enterNow: function () {
      return this.isAfterAjasReleaseDate ? 'Submission period ended' : false
    },
    showStartNow: function () {
      return !(!this.isLoggedIn && this.$isStories) && ((!this.isSearching && this.navbarSettings?.showStart) || (this.currentUser?.isPortal && this.isLoggedIn && this.$isYSC) || this.isAdmin)
    },
    showChat: function () {
      return !this.isSearching && !this.$isIsef && this.navbarSettings?.showChat
    },
    showSearch: function () {
      return this.navbarSettings?.showSearch
    },
    isStory: function () {
      return this.$route.name === 'viewStory' || this.$route.name === 'viewProject'
    },
    showNotificationsIcon: function () {
      return this.isLoggedIn && !this.$isGirlsAndSteam && this.navbarSettings?.showNotifications
    },
    showFeedbackModal: function () {
      return !(this.$isYSC || this.$isEngCom)
    },
    isComingSoon: function () {
      // Temporary Hide Start Button, Search, Notification, Community Chat and Login button.
      // Remove after BroadcomMasters event starts on Oct 26
      return false
    },
    wordmarkStyle: function () {
      if (this.navbarSettings.wordmarkMaxHeight === 52) {
        return {
          height: this.navbarSettings.wordmarkMaxHeight + 'px'
        }
      } else {
        return {
          'max-height': this.navbarSettings.wordmarkMaxHeight + 'px',
          'margin-left': '1.75rem'
        }
      }
    },
    currentUser: function () {
      return this.$store.getters.currentUser
    },
    group: function () {
      return this.$store.state.user
    },
    showMyClasses: function () {
      return (this.$isYSC || this.$isOhioScience) && this.currentUser.type?.includes(this.UserTypeTeacher)
    },
    isCustomModalVisible: function () {
      return this.$store.getters.modalIsVisible('customQuestionSurvey')
    }
  },
  mounted: function () {
    if (this.isTeacherType) {
      this.teacherCode = true
    }
    this.loadingNavbar = true
    navbarApi.getNav().then((response) => {
      this.loadingNavbar = false
      this.navbarSettings = response.data.result.navbar
      this.$store.dispatch('updateShowWidget', this.navbarSettings.showStartWidget)
      this.navbarItems = response.data.result.navbarItems
      this.moreItems = response.data.result.moreItems
      if (this.$isIsef) {
        // exhibit hall max size is 1400
        this.showMoreViewSize = 1400
      }
      this.navbarItems.forEach(navItem => {
        if (navItem.maxViewWidth > this.showMoreViewSize) {
          this.showMoreViewSize = navItem.maxViewWidth
        }
      })
    }, (error) => {
      this.loadingNavbar = false
      console.error(error)
    })

    let setModals = false
    switch (this.appName) {
      case 'ysc':
        if (!this.isStudentType) {
          setModals = true
        }
        break
      default:
        setModals = true
    }

    if (setModals) {
      this.modals = [this.$refs.loginModal, this.$refs.updateProfileModal, this.$refs.sourceModal,
        this.$refs.categoriesModal, this.$refs.onboardingModal, this.$refs.teacherModal]
    }
    this.checkModals()
    this.progressbar()
    this.isLoggedInUpdated()

    // versionCheck.start()
    setTimeout(() => {
      this.checkIfLoggedIn()
    }, 10000)

    if (!userAgent.isApp() && !authlib.isSu()) {
      this.webAuth = new auth0.WebAuth({
        domain: `${process.env.MAKE_ISSUER_URL}`,
        clientID: `${process.env.MAKE_CLIENT_ID}`,
        responseType: 'token id_token',
        redirectUri: `${process.env.MAKE_REDIRECT_URL}`,
        scope: 'openid profile email user_metadata',
        audience: `https://${process.env.OLD_MAKE_ISSUER_URL}/userinfo`,
        state: this.state(),
        nonce: this.state(),
        responseMode: 'web_message'
      })

      const that = this

      this.webAuth.checkSession({
        prompt: 'none'
      }, function (err, result) {
        if (!err) {
          // make sso session exists, log the user in if the user is not already logged in
          if (!authlib.isLoggedIn()) {
            const { idToken } = result
            axios.post(`${process.env.API_ROOT}/oauth/callback`, {
              id_token: idToken
            }).then(response => {
              that.$store.dispatch('makeLogin', response.data).then(function (tokens) {
                authlib.storeTokens(tokens)
                if (response.created) {
                  this.modalIndex = 1
                  this.modalDone(true, true)
                } else if (!response.created && that.$route.path === '/blankpage') {
                  that.$router.push({ path: '/start/template', query: that.$route.query })
                }
              })
            })
          }
        } else {
          // make sso session does not exist. if the user is a make user and is logged in, log them out
          if (authlib.isLoggedIn()) {
            users.current().then(response => {
              const user = response.data.result.user
              if (user && user.signUpSource === 'make_oauth' && !this.$isHeise) {
                authlib.logout()
                that.webAuth.logout({
                  returnTo: `${process.env.API_ROOT}/oauth/logout`,
                  clientID: `${process.env.MAKE_CLIENT_ID}`
                })
              }
            })
          }
        }
      })
    }
    if (this.$route.query.follow) {
      this.tagAndAddToGroup()
    }
    if (this.$route.query.notifications) {
      if (!this.isLoggedIn) {
        this.$store.dispatch('showLoginModal')
      } else {
        this.showNotifications()
      }
    }
    if (this.$route.query.signup && !this.isLoggedIn) {
      this.$store.dispatch('showLoginModal', {
        source: 'QUERY_SIGNUP:{PATH}',
        signup: true
      })
    }

    if (this.showYscWelcomeModal()) {
      this.$store.dispatch('showYscPreRegistrationModal', { onSuccess: () => this.showSignup('cwsf-espc-2022', true) })
    }
  },
  methods: {
    clearNotificationMessage: function () {
      this.notificationMessage = ''
    },
    isLoggedInUpdated: function () {
      this.$store.dispatch('isUserApprover', { userID: this.$store.getters.currentUser.id }).then((response) => {
        if (response) {
          this.isUserApprover = response.isUserApprover
        }
      })
      if (this.isLoggedIn) {
        this.getUnreadNotifications()
        this.checkedUnread = true
        this.$store.dispatch('getUnreadCount', this.currentUser.userName).catch(() => {
          this.checkedUnread = false
        })

        const updateModals = function () {
          this.checkModals()
          this.showModal()
        }.bind(this)

        if (this.currentUser) {
          this.isUserJudge = this.currentUser.isJudgingAdmin
          // Resorting to URLSearchParams because this.$route.query is not
          // initialized, and accessing this.$route.query inside this.$nextTick or
          // this.$router.onReady doesn't solve the issue.
          const urlParams = new URLSearchParams(window.location.search)
          const userType = urlParams.get('userType')
          const signUp = urlParams.get('signup')
          if (signUp && userType) {
            this.$store.dispatch('updateUserType', {
              username: this.currentUser.userName,
              userType: userType
            }).then((response) => {
              updateModals()
            }, (error) => {
              if (error.status === 400) {
                var userTypeString = userType.charAt(0).toUpperCase() + userType.slice(1)
                this.notificationMessage = 'Oops! It looks like you\'re trying to register as a ' + userTypeString + ', but you\'re already signed up for a student account. If you still want to become a ' + userTypeString + ', please contact support@projectboard.world with the subject line "Change to ' + userTypeString + ' Account".'
              }
              console.error(error)
            })
          } else {
            updateModals()
          }
        }
      }
    },
    openMakeHome: function () {
      this.$i18n.locale = 'en'
      this.$nextTick(() => {
        this.$router.push({ name: 'home', query: { r: this.refreshKey() } })
      })
      if (this.$isYSC) {
        this.$nextTick(() => {
          this.forceRerender()
        })
      }
    },
    clickLogo: function () {
      if (this.$isSigmaXi) { // Temporary Removal of SigmaXi Logo Redirect
        return
      }
      if (this.navbarSettings?.logoUrl) {
        let path = this.navbarSettings.logoUrl
        if (this.navbarSettings?.useRefreshKey) {
          path = path + '?r=' + this.refreshKey()
        }
        this.$router.push({ path })
      } else {
        this.$nextTick(() => {
          this.$router.push({ name: 'home', query: { r: this.refreshKey() } })
        })
      }
    },
    getUnreadNotifications: function () {
      if (this.loading || !this.currentUser.userName) {
        return
      }
      const payload = { username: this.currentUser.userName }
      this.loading = true
      this.$store.dispatch('getUnreadNotifications', payload).then((response) => {
      }, (error) => {
        this.error = error.message
      })
    },
    forceRerender: function () {
      this.$emit('forceRerender')
      this.$store.dispatch('clearCategories')
    },
    openLink: function (url, refresh = false) {
      // prevent logged out users from clicking profile
      if (!this.isLoggedIn && url === '/profile') {
        return
      }
      this.link(url, refresh)
      this.showMenu = false
      this.showLogout = false
      this.isSearching = false
      this.exitExplore()
    },
    cancel: function () {
      this.$emit('modalCancel')
      this.close()
    },
    close: function () {
      this.showChooseTemplateModal = false
    },
    closeTeacherCodeModal: function () {
      this.showTeacherCode = false
    },
    openChooseTemplateModal: function () {
      if (this.isAfterAjasReleaseDate) {
        return
      }
      if (this.$isAjas) {
        this.$store.dispatch('showStartProjectModal')
        return
      }
      if (this.$isOhioScience) {
        this.$router.push('/start/template?projectID=67469&startButton=true&draft=true&utm_source=pb_ohio_landing')
        return
      }
      if (this.$isIsef) {
        this.$router.push('/start/template?projectID=108467&startButton=true&draft=true&signup=true')
        return
      }
      if (this.isLoggedIn || !this.isYSC) {
        this.showChooseTemplateModal = true
      } else {
        const utm = authlib.getUtmSource()
        if (utm !== 'login_from_ysc_portal') {
          this.$store.dispatch('showLoginModal', {
            source: 'LOGIN:{PATH}'
          })
          this.startButtonClicked = true
        } else {
          this.$store.dispatch('showLoginModal')
        }
        this.openTemplateAfterSignup = true
      }
    },
    checkModals: function () {
      this.signupModals = this.modals.filter((modal, index) => {
        return this.modalFilter(index)
      })
    },
    // Update the partners modals
    modalFilter: function (index) {
      const onboardingModal = this.modals.indexOf(this.$refs.onboardingModal)
      const categoriesModal = this.modals.indexOf(this.$refs.categoriesModal)
      const sourceModal = this.modals.indexOf(this.$refs.sourceModal)
      const updateProfileModal = this.modals.indexOf(this.$refs.updateProfileModal)
      const loginModal = this.modals.indexOf(this.$refs.loginModal)
      const teacherModal = this.modals.indexOf(this.$refs.teacherModal)
      const hide = (modal) => {
        return index !== modal
      }

      let filter = null
      switch (this.appName) {
        case 'ysc':
          if (this.isStudentType) {
            filter = hide(loginModal) && hide(updateProfileModal) && hide(onboardingModal) && hide(categoriesModal) && hide(sourceModal) && hide(teacherModal)
          } else if ([this.UserTypeTeacher, this.UserTypeJudge].includes(this.$store.getters.currentUser.type)) {
            filter = hide(onboardingModal) && hide(categoriesModal) && hide(teacherModal)
          } else {
            filter = hide(onboardingModal) && hide(categoriesModal) && hide(sourceModal) && hide(teacherModal)
          }
          break
        case 'heise':
        case 'make':
          filter = hide(onboardingModal) && hide(teacherModal)
          break
        case 'neweng':
          filter = hide(sourceModal) && hide(onboardingModal) && hide(teacherModal)
          break
        case 'buckeye':
        case 'isef':
        case 'societyforsciencebcm':
        case 'projectboard':
        case 'sts':
          filter = hide(onboardingModal) && hide(sourceModal) && hide(categoriesModal) && hide(teacherModal)
          break
        case 'cnmiiwsf':
          if (this.isStudentType && this.$route.query.userType !== 'booth') {
            filter = hide(onboardingModal) && hide(categoriesModal) && hide(sourceModal) && hide(teacherModal)
          } else {
            filter = hide(onboardingModal) && hide(categoriesModal) && hide(updateProfileModal) && hide(teacherModal)
          }
          break
        case 'ohiosci':
          if (this.isStudentType || this.isTeacherType || this.isMentorType || this.isJudgeType) {
            filter = hide(onboardingModal) && hide(categoriesModal)
          } else {
            filter = hide(onboardingModal) && hide(categoriesModal) && hide(sourceModal)
          }
          // only show Class Modal on signup teacher type, but not on refresh or re-login
          if (!this.isRouterTeacherType) {
            filter = filter && hide(teacherModal)
          }
          break
        default:
          filter = hide(onboardingModal) && hide(categoriesModal) && hide(teacherModal)
          break
      }

      return filter
    },
    modalDone: function (loginSuccess, newUser) {
      const hasMoreModals = this.modalIndex < this.signupModals.length
      if (loginSuccess && this.modalIndex === 1) {
        this.loginModalCallback(loginSuccess, newUser)
      }
      if (loginSuccess && hasMoreModals) {
        if (!this.isCustomModalVisible) {
          this.showModal()
        }
      } else {
        this.checkTemplateModal()
        this.modalIndex = 0
        this.$store.dispatch('isSignupComplete', true)
      }

      this.$store.dispatch('hideLoginModal')
    },
    showModal: function () {
      if (this.signupModals.length !== 0 && this.modalIndex < this.signupModals.length) {
        if (this.modalIndex - 1 > 0) {
          if (this.signupModals[this.modalIndex - 1].active) {
            console.log('showModal called when a modal was already active - ignoring')
            return
          }
        }

        console.log('Calling show modal on ' + this.signupModals[this.modalIndex].$vnode.tag)
        this.signupModals[this.modalIndex++].showModal()
      }
    },
    loginModalCallback: function (loginSuccess, newUser) {
      if (typeof this.loginModalData.onSuccess === 'function') {
        this.loginModalData.onSuccess(newUser)
      }
      if (typeof this.loginModalData.callback === 'function') {
        this.loginModalData.callback(loginSuccess === true, newUser)
      }
    },
    checkTemplateModal: function () {
      if (this.openTemplateAfterSignup && this.isLoggedIn) {
        this.showChooseTemplateModal = true
        this.openTemplateAfterSignup = false
      }
    },
    search: function () {
      if (this.searchQuery !== '') {
        const trimmedQuery = this.searchQuery.trim()
        if (this.isEngCom) {
          this.$router.push({ name: 'search', query: { r: this.refreshKey(), q: trimmedQuery }, params: { searchQuery: trimmedQuery } })
        } else {
          this.$router.push({ name: 'legacySearch', query: { r: this.refreshKey() }, params: { searchQuery: trimmedQuery } })
        }
        this.isSearching = false
        this.searchQuery = ''
      }
    },
    toggleExplore: function () {
      this.isExploring = !this.isExploring
      if (this.isExploring) {
        this.hideMenu()
      }
    },
    toggleExploreMore: function () {
      if (this.isExploringMore) {
        this.isExploringMore = false
      } else {
        this.closeNavDropdowns()
        this.isExploringMore = true
        this.hideMenu()
      }
    },
    toggleExploreTopic: function () {
      if (this.isExploringTopic) {
        this.isExploringTopic = false
        this.isExploringDropdown = false
      } else {
        this.closeNavDropdowns()
        this.isExploringTopic = true
        this.hideMenu()
      }
    },
    selectLink: function (linkName) {
      this.selectedLink = linkName
    },
    exitExplore: function () {
      this.isExploring = false
      this.isExploringMore = false
      this.isExploringTopic = false
      this.isExploringDropdown = false
      this.closeNavDropdowns()
    },
    toggleSearch: function () {
      this.isSearching = !this.isSearching
      this.showMenu = false
      if (this.isSearching) {
        this.$nextTick(() => {
          this.$refs.search.focus()
        })
      }
    },
    toggleMenu: function () {
      if (this.isLoggedIn) {
        this.showMenu = !this.showMenu
        if (this.showMenu) {
          this.exitExplore()
        }
        this.$refs.notificationsDropdown.show = false
      }
    },
    hideMenu: function () {
      this.showMenu = false
    },
    refreshKey: function () {
      return (Math.random() + 1).toString(36).substring(7)
    },
    showSignup: function (source, displaySignup = false) {
      if (!this.isLoggedIn) {
        this.passwordSet = false
        this.$store.dispatch('showLoginModal', { source, signup: displaySignup })
      }
    },
    showLogin: function () {
      if (!this.isLoggedIn) {
        this.passwordSet = false
        this.$store.dispatch('showLoginModal', { source: 'login-nav-bar' })
      }
      if (this.openFlag) {
        this.openFlag = false
      }
    },
    checkIfLoggedIn: function () {
      if (this.$isApp && !this.isLoggedIn) {
        this.showSignup()
      } else {
        this.libs.auth.checkLogin().then((loggedIn) => {
          if (this.shouldRedirectToAbout(loggedIn)) {
            this.$router.push({ name: 'about' })
          }
        })
      }
    },
    openViewAllPages: function (toPath) {
      if (!this.isLoggedIn) {
        return
      }
      if (this.$route.fullPath !== toPath) {
        this.$router.push(toPath)
        this.showMenu = false
        this.showLogout = false
        this.isSearching = false
        this.exitExplore()
      }
    },
    passwordSetComplete: function () {
      this.passwordSet = true
    },
    goBack: function () {
      if (window.history.length < 2) {
        this.$router.push({ name: 'home' })
      } else {
        this.$router.go(-1)
      }
    },
    shouldRedirectToAbout: function (loggedIn) {
      return false
    },
    onClickOutsideNotifications: function (e, el, extras) {
      // if the user has clicked outside of notifications modal + notifications navbar icon
      if (!(e.target.className.match(/fa-bell|notification-bell/g))) {
        this.$refs.notificationsDropdown.show = false
      }
      this.showMenu = false
    },
    logout: function () {
      this.subscriptions = { message: false, user: false }
      this.checkedUnread = false
      this.passwordSet = false
      this.openLink('/logout')
      this.$store.dispatch('isSignupComplete', false)
      this.$store.dispatch('isSigningUp', false)
      this.modalIndex = 0

      if (this.currentUser.signupSource === 'make_oauth' && !userAgent.isApp()) {
        this.webAuth.logout({
          returnTo: `${process.env.API_ROOT}/oauth/logout`,
          clientID: `${process.env.MAKE_CLIENT_ID}`
        })
      }
    },
    savingPath: function (value) {
      this.previousPath = value
    },
    state: function () {
      const validChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      let array = new Uint8Array(40)
      window.crypto.getRandomValues(array)
      array = array.map(x => validChars.charCodeAt(x % validChars.length))
      return String.fromCharCode.apply(null, array)
    },
    tagAndAddToGroup: function () {
      if (this.$route.query.follow) {
        if (this.isLoggedIn) {
          let tags = []
          if (typeof this.$route.query.follow === 'string') {
            tags = [this.$route.query.follow]
          } else {
            tags = this.$route.query.follow
          }

          const data = { tags }
          this.$store.dispatch('tagAndAddToGroup', { username: this.currentUser.userName, data })
        } else {
          this.$store.dispatch('showLoginModal', {
            source: 'FOLLOW:{PATH} ' + this.$route.query.follow,
            callback: this.tagAndAddToGroup,
            signupMessage: 'Sign up to follow this project'
          })
        }
      }
    },
    startButtonAction: function (value) {
      this.startButtonClicked = value
    },
    progressbar: function () {
      document.addEventListener('scroll', _ => {
        const docElem = document.documentElement
        const docBody = document.body
        const scrollTop = (docBody.scrollTop || docElem.scrollTop)
        const height = docElem.scrollHeight - docElem.clientHeight
        const progressBar = document.querySelector('#progress-bar')
        const progress = scrollTop / height * 100
        let bgVal = ''

        if (progress > 0) {
          switch (this.appName) {
            case 'neweng':
              bgVal = 'linear-gradient(to right, #7A2A90 ' + progress + '%, transparent 0)'
              break
            case 'ajas':
              bgVal = 'linear-gradient(to right, #C3A754 ' + progress + '%, transparent 0)'
              break
            case 'ysc':
              bgVal = 'linear-gradient(to right, #6639B7 ' + progress + '%, transparent 0)'
              break
            case 'projectboard':
              bgVal = 'linear-gradient(to right, #8400f0 ' + progress + '%, transparent 0)'
              break
            default:
              bgVal = 'linear-gradient(to right, #EA002A ' + progress + '%, transparent 0)'
          }
        }
        if (progressBar) {
          progressBar.style.background = bgVal
        }
      })
    },
    showNotifications: function () {
      this.$refs.notificationsDropdown.show = !this.$refs.notificationsDropdown.show
    },
    checkStartButtonNavigation: function () {
      if (this.$isProjectboard) {
        if (!this.isLoggedIn || (this.isLoggedIn && !this.isAdmin)) {
          this.$router.push({ name: 'contact' })
        } else {
          this.openChooseTemplateModal()
        }
      } else {
        this.openChooseTemplateModal()
      }
    },
    showLoginModal: function () {
      return this.$store.dispatch('showLoginModal')
    },
    updateSelectedLink: function () {
      this.isExploring = false
      this.isExploringDropdown = false
      this.closeNavDropdowns()
    },
    closeStudentCode: function () {
      this.showStudentCode = false
    },
    showYscWelcomeModal: function () {
      if (!this.$isYSC) {
        return false
      }

      if (localStorage.get('skip_ysc_welcome') === '1') {
        return false
      }

      if (this.isLoggedIn) {
        return false
      }

      if (this.$route.query.yscwelcome) {
        return true
      }

      const currentTime = new Date()
      const yscEventDate = new Date('2022/05/18')

      return this.$isYSC && currentTime >= yscEventDate && !this.$route.query.userType && this.$route.path.includes('/cwsf-espc')
    }
  },
  updated: function () {
    if (this.isLoggedIn) {
      if (!this.subscriptions.user) {
        this.subscriptions.user = true
        socket.subscribeAll('user', this.currentUser.uuid)
      }
      if (!this.subscriptions.message) {
        this.subscriptions.message = true
        socket.subscribeAll('conversation', '')
      }
    }
  },
  // destroyed: function () {
  //   versionCheck.stop()
  // },
  directives: {
    ClickOutside
  }
}
</script>
<style lang="scss" scoped>
@import '~styles/utils.scss';
@import '~@/styles/projectboard-mixins';
$tile-height: $nav-height-px;
$tile-width: 61px; // 61px is hard-coded below inside of a calc()
$tile-width-m: 52px;
$tile-width-xl: 75px; //For the sign in button
$tile-width-m-xl: 70px; // For the sign in button on mobile
$category-width-more: 103px;
$category-width-communities: 166px;
$category-width-exhibitHall: 166px;

/* General styles */
img {
  max-width: 100%;
  max-height: 100%;
}

a {
  display: block;
}

/* Navigation Bar */
.nav-container {
  max-width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 15;
  border-bottom: solid 1px var(--background-fourth);
  height: $tile-height;
}

.nav,
.nav-start,
.nav-end {
  background: var(--background-secondary);
}

.nav {
  height: $tile-height;
  position: relative;
  z-index: 15;
}

.nav-start {
  justify-content: flex-start;
  margin-left: 2rem; // Making room for the Logo
  flex: 1 1 85%;
  @include touch {
    margin-left: 0 !important;
  }
}

.nav-end {
  justify-content: flex-end;
  flex: 1 1 15%;
  z-index: 16;
}

.nav-item {
  display: flex;
  align-items: center;
  min-width: 35px;
  height: $tile-height;
  color: var(--font-colour);
  background-color: var(--background-secondary);
  &:not(.login-wrapper),
  &:not(.start-now-wrapper) {
    width: 52px;
    @include touch {
      &.explore-burger {
        width: 38px;
        min-width: 38px;
      }
    }
    @include mobile {
      width: 45px;
    }
  }
  &:hover,
  &:focus {
    outline: none;
    color: $primary;
    background: var(--background-fourth);
  }
}

/* Title & Logo */
.nav-title {
  height: 100%;
  z-index: 16;
  pointer-events: none;
  font-weight: bold;
  font-size: 1.2rem;
  color: #7a7a7a;
  text-align: left;
  @include touch {
    width: 100%;
  }
  a {
    display: inline-block;
    pointer-events: auto;
    position: relative;
    &:hover,
    &:focus {
      opacity: 0.8;
      outline: 0;
    }
  }
  .logo-container {
    display: none;
    @include touch {
      width: 100%;
      height: 52px;
      display: flex;
      flex-direction: column;
      align-items: center;
      position: absolute;
      justify-content: center;
    }
    .logo {
      @include touch {
        margin: auto auto;
      }
    }
  }
}

.wordmark-container {
  height: 52px;
  display: flex;
  flex-direction: column;
  align-items: center;
  .wordmark {
    margin: auto 0;
  }
  @include touch {
    display: none;
  }
}

/* NavToolbar Links */
.category-nav {
  width: 100%;
}

.category-list {
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  display: flex;
}

.category-item {
  height: 100%;
  white-space: nowrap;
  position: relative;
  &.category-more-isef {
    @media (min-width: 1401px) {
      display: none;
    }
  }
  &.category-more {
    width: $category-width-more;
  }
  &:hover,
  &:focus {
    outline: none;
  }
}

.category-link {
  align-items: center;
  padding: 3px 16px 0;
  text-transform: uppercase;
  font-size: 0.9rem;
  background-color: var(--background-secondary);
  color: var(--font-colour);
  width: 100%;
  height: $tile-height;
  z-index: 16;
  display: flex;
  justify-content: center;
  letter-spacing: 1px;
  position: relative;
  transition: 0.2s;
  i {
    margin-left: 0.4rem;
    color: #2a2a2a;
  }
}

.link-underline {
  &:hover,
  &:focus,
  &.link-exact-active {
    &::after {
     content: "";
     display: block;
     border-bottom: 1px solid $primary;
     position: absolute;
     left: 7px;
     right: 7px;
     bottom: 0.7rem;
    }
  }
}

.link-bold {
  &:hover,
  &:focus,
  &.link-exact-active,
  &.active-more {
    outline: none;
    font-weight: bold;
    transition: 0.2s font-weight;
  }
}

.chevron-color {
  &:hover,
  &.active-more {
    i {
      color: $primary;
    }
  }
}

/* Search Bar */
.search-bar {
  position: absolute;
  z-index: 20;
  top: 0;
  left: 150px;
  width: calc(100% - 409px);
  height: $tile-height;
  @include mobile {
    left: 82px;
    width: calc(100% - 122px);
  }
}

.search-bar.logged-out {
  width: calc(100% - 320px);
  @include mobile {
    width: calc(100% - 145px);
  }
}

// Makes only the placeholders italics, but must be defined seperaltely
::-webkit-input-placeholder {
  font-style: italic;
}

:-moz-placeholder {
  font-style: italic;
}

::-moz-placeholder {
  font-style: italic;
}

:-ms-input-placeholder {
  font-style: italic;
}

.search-field {
  position: absolute;
  right: 52px;
  top: 0;
  width: calc(100% - 5vw);
  height: 100%;
  font-size: 1.3rem;
  border: none;
  border-left: 1px solid var(--background-fourth);
  padding-left: 3rem;
  &:focus {
    outline: none;
  }
  @include mobile {
    left: 0;
    padding-left: 1rem;
    font-size: 1rem;
    width: 100%;
  }
}

/* Icons and Buttons */
button:not(.button),
.mail-button {
  border: none;
  color: var(--font-colour);
  background-color: var(--background-secondary);
  width: 100%;
  height: 100%;
  &:hover,
  &:focus {
    outline: 0;
    background: var(--background-fourth);
  }
}

.explore-burger {
  display: none;
  margin-right: 0.5rem;
  @include touch {
    display: block;
  }
}

.explore-button,
.mail-button,
.search-button,
.faq-button {
  display: flex;
  justify-content: center;
  align-items: center;
}

.explore-button {
  font-size: 1.3rem;
}

.start-now-wrapper {
  margin-right: 1.1rem;
  margin-left: 1.1rem;
  align-items: center;
}

.start-now-button {
  background: $primary;
  padding: 0 0.75rem;
  color: $white;
  width: auto;
  height: 27px;
  border-radius: 2px;
  transition: 0.2s;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  justify-content: center;
  align-items: center;
  i {
    font-size: 1rem;
  }
  &:hover,
  &:focus {
    background: $primary-gradient;
    font-weight: 600;
    transition: all 0.2s ease-in;
    transform: scale(1.1);
  }
  @include desktop-only {
    &.shorten {
      font-size: 0.8rem;
      padding: 0 0.6rem;
    }
  }
  @include mobile {
    height: 27px;
    width: 27px;
    &:hover,
    &:focus {
      transform: none;
    }
  }
}

.button-wrapper {
  @include touch {
    margin-right: 0.5rem;
  }
  @media (max-width: 350px) {
    margin-right: 0;
  }
  .start-now-button {
    background: $primary;
    &.ysc {
      background: linear-gradient(45deg, #713cd3, #6639b7);
    }
  }
}

.search-button {
  position: relative;
  color: var(--font-colour);
  background-color: var(--background-secondary);
  font-size: 1rem;
  padding: 0;
  @include touch {
    font-size: 1.3rem;
  }
}

.mail-button,
.faq-button {
  font-size: 1rem;
}

.login-wrapper {
  align-items: center;
}

.login-button {
  border: none;
  margin: 0 1rem 0 0.5rem;
  transition: 0.3s background-color;
  @include mobile {
    margin-right: 0.5rem;
  }
  @media (max-width: 350px) {
    margin: 0 0.2rem 0 0.2rem;
  }
  &:hover,
  &:focus {
    transition: 0.3s background-color;
    background-color: $primary-gradient;
  }
}

/* Mail */
.u-got-mail {
  .mail-button {
    position: relative;
    font-weight: bold;
  }
}

.unread-count {
  justify-content: center;
  align-items: center;
  font-size: 0.6rem;
  border: 1px solid $primary;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  background-color: $primary;
  color: white;
  position: absolute;
  font-weight: bold;
  bottom: 9px;
  right: 8px;
  &.ysc {
     background-color: #6639b7;
     border: 1px solid #6639b7;
   }
}

.unread-count-bell {
  right: 61px;
}

/* Profile Dropdown */
.profile-img,
.profile-burger {
  display: flex;
  justify-content: center;
  align-items: center;
}

.profile-burger {
  border-left: 1px solid var(--background-fourth);
  @media (hover: none) {
    display: none;
  }
}

.touch {
  display: flex;
  @media (hover: hover) {
    display: none;
  }
}

.profile-burger.logged-out {
  border-left: 0;
}

.profile-img {
  min-width: 28px;
  height: 30px;
  img {
    border-radius: 50%;
  }
}

.profile-dropdown {
  &:focus {
    border: 10px solid var(--background-primary);
  }
}

.dropdown-menu {
  display: none;
  background-color: var(--background-secondary);
  min-width: 160px;
  padding: 0.5rem 0;
  position: absolute;
  top: $navbar-height;
  right: 0;
  left: auto;
  z-index: 15;
  .nav-item {
    width: 100%;
    min-width: 160px;
    height: 35px;
    padding: 0.5rem 1.5rem;
    font-size: 0.8rem;
    .icon .fal {
      font-size: 0.8rem;
    }
  }
}

.is-shown {
  display: block;
}

/* Misc. */
.navbar-spacer {
  height: $navbar-height;
}

.hidden-mobile {
  @include touch {
    display: none;
  }
}

.search-button,
.mail-button,
.faq-button {
  i {
    padding-top: 1px;
  }
}

// custom screen for iPhone 5/SE sizes
@media screen and (max-width: 375px) {
  .nav-item:not(.login-wrapper),
  .nav-item:not(.start-now-wrapper) {
    width: 38px;
  }
  .u-got-mail {
    padding-left: 0;
  }
  .unread-count {
    bottom: 8px;
    right: 3px;
  }
  .unread-count-bell {
    right: 42px;
  }
}

@media (max-width: 350px) {
  .nav-item:not(.login-wrapper),
  .nav-item:not(.start-now-wrapper) {
    width: 35px;
  }
}

.mobile-search {
  display: none;
  @include touch {
    display: block;
  }
}

.desktop-search {
  display: block;
  @include touch {
    display: none;
  }
}

.disabled {
  cursor: not-allowed;
  background-color: #ffffff;
}

.notification-bell {
  display: flex;
  justify-content: center;
  align-content: center;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  a {
    color: var(--font-colour);
  }
  i {
    font-weight: 300;
  }
}

#progress-bar {
  background: linear-gradient(
      to right,
      #7a2a90,
      #7a2a90 var(--progress),
      transparent 0
  );
  position: sticky;
  overflow: hidden;
  width: 100%;
  height: 3px;
  z-index: 1;
}

#make-progress-bar {
  background: linear-gradient(
      to right,
      #7a2a90,
      #7a2a90 var(--progress),
      transparent 0
  );
  position: sticky;
  overflow: hidden;
  width: 100%;
  height: 3px;
  z-index: 1;
}

@media screen and (max-width: 830px) {
  .hide-start-menu {
    display: none;
  }
  .hide-start-menu-mobile {
    display: inline-block;
  }
}

@media screen and (min-width: 830px) {
  .hide-start-menu-mobile {
    display: none;
  }
  .hide-start-menu {
    display: inline-block;
  }
}
</style>
